import { useMemo, useState } from "react";
import TouchPreview, { type PreviewFrame } from "./TouchPreview";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import { lookupToken, tokenBracketed, tokensIn, vocabularyOf, type TokenVocabulary } from "./touchCopy";
import {
  anchorName,
  deliverableTouches,
  sendDate,
  type EnabledChannels,
  type KeyDateRef,
  type Sequence,
  type Touch,
} from "./emailSmsTypes";

type Props = {
  campaignName: string;
  /** Only the sequences that are on — the client is shown what will actually send. */
  sequences: Sequence[];
  /** And only the channels the campaign delivers on, for the same reason (MPN-2379): a
      client asked to approve an SMS the campaign cannot send is being asked the wrong
      question. */
  channels: EnabledChannels;
  keyDates: KeyDateRef[];
  values: Record<string, string>;
  fromName: string;
  fromAddress: string;
  smsSender: string;
  footerAddress: string;
  approvalNote: string;
  sendLabel: string;
  sendDisabled: boolean;
  onSend: () => void;
  onClose: () => void;
};

/** Tokens in this touch's copy that have no value on this campaign. */
function missingTokens(
  touch: Touch,
  values: Record<string, string>,
  vocabulary: TokenVocabulary,
): string[] {
  const copy =
    touch.ch === "email"
      ? `${touch.subject ?? ""} ${touch.pre ?? ""} ${touch.body ?? ""}`
      : (touch.text ?? "");
  return Array.from(new Set(tokensIn(copy))).filter(
    (key) => lookupToken(key, values, vocabulary) === "",
  );
}

/*
    What the client is shown in content approval, rendered with this campaign's own values
    rather than bracketed placeholders. Read-only on purpose: approval is a decision about
    copy that already exists, and an editable preview invites changes that never reach the
    catalog.
*/
export function ClientApprovalPreview({
  campaignName,
  sequences,
  channels,
  keyDates,
  values,
  fromName,
  fromAddress,
  smsSender,
  footerAddress,
  approvalNote,
  sendLabel,
  sendDisabled,
  onSend,
  onClose,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [frame, setFrame] = useState<PreviewFrame>("desktop");

  const { tokens: adTokens } = useAdTokens();
  const vocabulary = useMemo(() => vocabularyOf(adTokens), [adTokens]);

  // One flat, date-ordered list: the client reads it as the student will receive it, not
  // grouped by the sequence it happens to come from.
  const items = useMemo(() => {
    const rows = sequences
      .flatMap((sequence) =>
        deliverableTouches(sequence.touches, channels)
          .filter((touch) => touch.on !== false)
          .map((touch) => ({ touch, sequence, when: sendDate(touch, keyDates) })),
      )
      .sort((a, b) => (a.when?.getTime() ?? 0) - (b.when?.getTime() ?? 0));
    return rows.map((row, index) => ({
      ...row,
      n: index + 1,
      missing: missingTokens(row.touch, values, vocabulary),
    }));
  }, [sequences, channels, keyDates, values, vocabulary]);

  const selected = items.find((i) => i.touch.id === selectedId) ?? items[0] ?? null;
  const withMissing = items.filter((i) => i.missing.length > 0);

  return (
    <div className="ap" role="dialog" aria-modal="true" aria-label="Client approval preview">
      <div className="ap__head">
        <span className="tx__accent" />
        <div className="ap__ident">
          <span className="ap__title">Client approval preview</span>
          <span className="ap__sub">
            {campaignName} · What the client sees in content approval, with this campaign's values
          </span>
        </div>
        <button type="button" className="esms-seq__chev" aria-label="Close" onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      </div>

      {withMissing.length > 0 && (
        <div className="ap__missing">
          <span className="esms-banner__icon">!</span>
          <span>
            {withMissing.length} {withMissing.length === 1 ? "touch has" : "touches have"} a merge
            field with no value on this campaign. They are marked amber below and will send as an
            empty space.
          </span>
        </div>
      )}

      <div className="ap__main">
        <div className="ap__rail">
          <div className="ap__values">
            <div className="tx__section">Values on this campaign</div>
            <dl className="ap__val-grid">
              {Object.entries(values).map(([key, value]) => (
                <div className="tx__val-row" key={key}>
                  <dt>{tokenBracketed(key)}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <div className="ap__val-note">[first_name] and links fill in per student.</div>
          </div>

          <div className="ap__list-head">
            Touches
            <span className="esms-count">{items.length}</span>
          </div>

          {items.map((item) => (
            <button
              type="button"
              key={item.touch.id}
              className={`ap__item${selected?.touch.id === item.touch.id ? " ap__item--on" : ""}`}
              onClick={() => setSelectedId(item.touch.id)}
            >
              <span className="ap__item-n">{item.n}</span>
              <span className={`touch-ch touch-ch--${item.touch.ch}`}>
                {item.touch.ch === "email" ? "Email" : "SMS"}
              </span>
              <span className="ap__item-title">{item.touch.title}</span>
              <span className="ap__item-date">
                {item.missing.length > 0 && (
                  <span className="ap__item-warn" title="Has a token with no value">
                    !
                  </span>
                )}
                {item.when
                  ? item.when.toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : "—"}
              </span>
            </button>
          ))}
        </div>

        <div className="ap__pane">
          {selected ? (
            <>
              <div className="ap__pane-head">
                <div className="ap__pane-ident">
                  <div className="ap__pane-title">{selected.touch.title}</div>
                  <div className="ap__pane-meta">
                    {selected.sequence.name} ·{" "}
                    {selected.when
                      ? selected.when.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : `${selected.touch.dir === "on" ? "On" : `${selected.touch.days} days ${selected.touch.dir}`} ${anchorName(selected.touch.anchor)}`}
                  </div>
                </div>
                {selected.touch.ch === "email" && (
                  <div className="btn-group btn-group-sm" role="group" aria-label="Preview width">
                    {(["desktop", "mobile"] as PreviewFrame[]).map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`btn btn-sm ${frame === option ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setFrame(option)}
                      >
                        {option === "desktop" ? "Desktop" : "Mobile"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <TouchPreview
                touch={selected.touch}
                values={values}
                vocabulary={vocabulary}
                fromName={fromName}
                fromAddress={fromAddress}
                smsSender={smsSender}
                footerAddress={footerAddress}
                frame={frame}
              />

              <div className="tx__sample-note">
                Amber marks a token with no value on this campaign. Test sends use the same values.
              </div>
            </>
          ) : (
            <div className="cat-empty">
              <div className="cat-empty__title">Nothing to approve yet</div>
              <div className="cat-empty__body">
                Turn on a sequence and its touches appear here.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tx__foot">
        <span className="tx__foot-note">{approvalNote}</span>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={sendDisabled}
          onClick={onSend}
        >
          {sendLabel}
        </button>
      </div>
    </div>
  );
}

export default ClientApprovalPreview;
